/**
 * useZoneState — EINE Quelle für den Zustandsmaschinen-Zustand in der Watchlist.
 *
 * Audit 08.08.2026: Vorher rechnete `ZoneStateHeadline` den Zustand für die
 * Anzeige, während der „Nur Kaufbares"-Filter auf der Kauf-Ampel (Score) lief.
 * Zwei Urteile zur selben Frage, die sich widersprechen durften. Dieser Hook
 * wurde die gemeinsame Quelle: Anzeige UND Filter lesen denselben Zustand.
 *
 * UMBAU 11.08.2026 — DIE TAGESAKTE HAT VORRANG
 * Damit war die Widersprüchlichkeit INNERHALB der Seite behoben, nicht die
 * zwischen Seite und Server: Der Cron rechnete auf dem Server-Bestand
 * (`v5_ohlcv_history`), der Browser auf den Live-Daten des Anbieters. Gleiche
 * Regel, gleiches Fenster — aber zwei Datenstände, und damit zwei Antworten.
 *
 * Jetzt gilt: Gibt es für das Symbol eine Zeile in `v6_signal_snapshots`, ist
 * SIE die Antwort. Der Kurs-Abruf unterbleibt dann ganz — bei rund 60
 * Watchlist-Einträgen ist das auch der spürbarste Teil.
 *
 * DER LIVE-PFAD BLEIBT — ALS ÜBERGANG, NICHT ALS ZWEITE WAHRHEIT
 * Für ein Symbol ohne Zeile (neu aufgenommen, Cron noch nicht gelaufen) wird
 * weiter gerechnet. Dieser Fall wird NICHT verschwiegen: `quelle` sagt, woher
 * der Zustand stammt, und die Anzeige schreibt es dazu. Sobald der Cron alle
 * Symbole erfasst, läuft der Pfad leer.
 */
import { useMemo } from 'react';

import { useDataState } from '@/hooks/use-data-state';
import { useSnapshots } from '@/hooks/use-snapshots';
import { getMarketDataProvider } from '@/lib/market-data';
import { computeSetupState, SETUP_BARS, type SetupState } from '@/lib/v6/setup-state';
import { alsZustand } from '@/lib/v6/snapshot-read';

export interface UseZoneStateResult {
  state: SetupState | null;
  /** true, solange die Bars noch geladen werden (Cold-Start-Gnade im Filter). */
  isLoading: boolean;
  /** false für Krypto und nicht unterstützte Formate — dort gilt die Maschine nicht. */
  applicable: boolean;
  /**
   * Woher der Zustand stammt. `snapshot` = Tagesakte vom Cron (der Regelfall),
   * `live` = im Browser gerechnet, weil es für dieses Symbol noch keine Zeile
   * gibt. Die Anzeige macht den Unterschied sichtbar.
   */
  quelle: 'snapshot' | 'live' | null;
  /** Tag der Akte — nur bei `quelle === 'snapshot'`. */
  standVom: string | null;
}

export function useZoneState(symbol: string, assetClass?: string): UseZoneStateResult {
  const provider = useMemo(() => getMarketDataProvider(), []);
  const snapshots = useSnapshots();

  const isUnsupportedFormat = useMemo(() => {
    const upper = symbol.toUpperCase();
    return (
      upper.includes(':XETRA') ||
      upper.includes(':FRA') ||
      upper.includes(':SWX') ||
      upper.endsWith('.DE')
    );
  }, [symbol]);

  // Die Zustandsmaschine ist auf Aktien/ETFs gemessen (Signal-Konzept §7) —
  // für Krypto wird bewusst keine Behauptung aufgestellt.
  const applicable = !isUnsupportedFormat && assetClass !== 'crypto';

  const snapshot = applicable ? snapshots.bySymbol.get(symbol.toUpperCase()) ?? null : null;

  // Solange die Akte lädt, wird NICHT vorsorglich der Anbieter befragt: Sonst
  // liefe bei jedem Seitenaufruf für jedes Symbol ein Kursabruf mit, der
  // gleich darauf verworfen würde.
  const brauchtLivePfad =
    applicable && !snapshots.isLoading && snapshot === null && provider.is_live;

  const ohlcv = useDataState({
    queryKey: ['ohlcv-for-score', symbol, 'daily', SETUP_BARS, provider.name],
    queryFn: (signal) => provider.getOhlcv(symbol, 'daily', SETUP_BARS, signal).then((s) => s.data),
    enabled: brauchtLivePfad,
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b || b.length === 0,
  });

  const spyOhlcv = useDataState({
    queryKey: ['regime-ohlcv', 'SPY', provider.name],
    queryFn: (signal) => provider.getOhlcv('SPY', 'daily', 250, signal).then((s) => s.data),
    enabled: brauchtLivePfad,
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b || b.length === 0,
  });

  const liveState = useMemo(() => {
    if (!brauchtLivePfad || !ohlcv.state.data) return null;
    try {
      // `heute` ist Pflicht in der Produktion: Ohne Bewertungstag kann die
      // Altersprüfung nichts blockieren (siehe SetupKontext).
      return computeSetupState(ohlcv.state.data, {
        benchmark: spyOhlcv.state.data ?? undefined,
        heute: new Date().toISOString().slice(0, 10),
      });
    } catch {
      // Die Zustandsmaschine darf die Zeile niemals zum Absturz bringen.
      return null;
    }
  }, [brauchtLivePfad, ohlcv.state.data, spyOhlcv.state.data]);

  return useMemo(() => {
    if (!applicable) {
      return { state: null, isLoading: false, applicable, quelle: null, standVom: null };
    }
    if (snapshot) {
      return {
        state: alsZustand(snapshot),
        isLoading: false,
        applicable,
        quelle: 'snapshot' as const,
        standVom: snapshot.as_of_date,
      };
    }
    return {
      state: liveState,
      isLoading:
        snapshots.isLoading ||
        (brauchtLivePfad &&
          (ohlcv.state.status === 'loading' || ohlcv.state.status === 'idle')),
      applicable,
      quelle: liveState ? ('live' as const) : null,
      standVom: null,
    };
  }, [applicable, snapshot, liveState, snapshots.isLoading, brauchtLivePfad, ohlcv.state.status]);
}

/**
 * Der Filter „Nur Kaufbares": Was verdient heute Aufmerksamkeit?
 *
 * Bewusst nur zwei Zustände statt der vier Ampelfarben — `zone_offen` ist
 * eine Handlung mit Preis, `boden_reift` ein Beobachtungsauftrag mit
 * Trigger-Preis. Alles andere braucht heute keinen Blick.
 */
export function istBeachtenswert(state: SetupState | null): boolean {
  if (!state) return false;
  return state.zustand === 'zone_offen' || state.zustand === 'boden_reift';
}
